import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { Logger } from "shared/Helpers/logging"
import { FirebaseStatus, useFirebase } from "shared/Modules/Cordova/hooks/useFirebase"
import { selectPushNotificationSettings, selectUser, selectUserMustPickLocation } from "shared/Modules/User/userSelectors"
import { OnboardingDoneStep } from "./Components/OnboardingDoneStep"
import { OnboardingDrawerShell } from "./Components/OnboardingDrawer"
import { PickLocationStep } from "./Components/PickLocationStep"
import { PushPermissionStep } from "./Components/PushPermissionStep"
import { OnboardingStep, PushPermissionResult } from "./onboardingTypes"

type FlowState = Readonly<{
    // Id of the user the flow was started for [reset when another user logs in]
    userId: number
    // Steps still to be shown, in order
    steps: OnboardingStep[]
    // Which steps the user actually saw (skipped steps are not shown at all)
    shown: OnboardingStep[]
    // Outcome of the push permission step, if it was shown
    pushResult?: PushPermissionResult
    // Step re-entered with the back arrow: shown even if no longer strictly needed
    forced?: OnboardingStep
}>

/**
 * Onboarding flow shown right after login: a fixed sequence of steps where each step decides for itself
 * whether it is needed. Currently:
 *
 *   1. Pick initial location (if the user has no location yet)
 *   2. Ask for permission to send push notifications and pick the types to receive
 *      (if the user has not answered yet; only when cordova-plugin-firebasex-messaging is present,
 *      i.e. never in the browser or in app versions without push support)
 *   -  A final confirmation screen (only if at least one step was shown)
 *
 * The flow runs once per login (it is forgotten on logout and starts over on the next login). Steps are skipped, not
 * postponed: closing a step moves on to the next one. The push step offers a back arrow to the
 * location step when that step was shown (the location can then be changed before continuing).
 */
export function useOnboardingFlow() {
    const logger = new Logger("onboarding")

    const user = useSelector(selectUser)
    const userMustPickLocation = useSelector(selectUserMustPickLocation)
    const { accept: acceptPushNotifications } = useSelector(selectPushNotificationSettings)
    const firebase = useFirebase()

    const [flow, setFlow] = useState<FlowState>()

    // Whether a step must be shown: true / false, or undefined while the answer is not known yet
    const isStepNeeded = useCallback((step: OnboardingStep, shown: OnboardingStep[], forced: OnboardingStep | undefined): boolean | undefined => {
        switch (step) {
            case OnboardingStep.PickLocation:
                return forced === OnboardingStep.PickLocation || userMustPickLocation
            case OnboardingStep.PushPermission:
                // Wait until we know whether the Firebase messaging plugin is there
                switch (firebase.status) {
                    case FirebaseStatus.Initializing:
                        return undefined
                    case FirebaseStatus.Unavailable:
                        // Browser or app version without push support: never ask
                        return false
                    case FirebaseStatus.Ready:
                        // Ask only when there is no answer yet (a "false" means the user already declined)
                        return acceptPushNotifications === undefined
                }
            case OnboardingStep.Done:
                // Only worth showing when the user actually saw a step
                return shown.length > 0
        }
    }, [userMustPickLocation, firebase.status, acceptPushNotifications])

    // Latest version for use inside state updaters (which must not close over stale values)
    const isStepNeededRef = useRef(isStepNeeded)
    isStepNeededRef.current = isStepNeeded

    // Drop leading steps that are known not to be needed, so the drawer never closes and reopens
    // between two shown steps
    function skipUnneeded(steps: OnboardingStep[], shown: OnboardingStep[], forced: OnboardingStep | undefined) {
        let remaining = steps
        while (remaining.length > 0 && isStepNeededRef.current(remaining[0], shown, forced) === false) {
            logger.info(`Onboarding step ${remaining[0]} not needed [skipping]`)
            remaining = remaining.slice(1)
        }
        return remaining
    }

    // Start the flow when a user has logged in and loaded; forget it on logout so the next login starts over
    useEffect(() => {
        if (!user) {
            if (flow) setFlow(undefined)
            return
        }
        if (flow?.userId === user.id) return

        const steps = [OnboardingStep.PickLocation, OnboardingStep.PushPermission, OnboardingStep.Done]
        logger.info(`User ${user.id} logged in [starting onboarding flow with steps: ${steps.join(", ")}]`)
        setFlow({ userId: user.id, steps: skipUnneeded(steps, [], undefined), shown: [] })
    }, [user?.id])

    // Finish (or skip) the current step; `expected` guards against a stale callback after the step already moved on
    const advance = useCallback((expected: OnboardingStep, wasShown: boolean, pushResult?: PushPermissionResult) => {
        setFlow((prev) => {
            if (!prev || prev.steps[0] !== expected) return prev
            const [done, ...rest] = prev.steps
            const shown = wasShown ? [...prev.shown, done] : prev.shown
            logger.info(`Onboarding step ${done} ${wasShown ? "finished" : "skipped"} [${rest.length} step(s) left]`)
            return {
                ...prev,
                steps: skipUnneeded(rest, shown, undefined),
                shown,
                pushResult: pushResult ?? prev.pushResult,
                forced: undefined,
            }
        })
    }, [])

    // X on a step: leave the whole flow (nothing saved, the user is asked again on the next login)
    const closeFlow = useCallback(() => {
        setFlow((prev) => {
            if (!prev) return prev
            logger.info("Onboarding flow closed by the user")
            return { ...prev, steps: [], forced: undefined }
        })
    }, [])

    // Back from the push step to the location step
    const goBackToLocation = useCallback(() => {
        setFlow((prev) => {
            if (!prev || prev.steps[0] !== OnboardingStep.PushPermission) return prev
            logger.info("Going back to the location step")
            return { ...prev, steps: [OnboardingStep.PickLocation, ...prev.steps], forced: OnboardingStep.PickLocation }
        })
    }, [])

    const currentStep = flow?.steps[0]
    const locationStepShown = flow?.shown.includes(OnboardingStep.PickLocation) ?? false
    const forced = flow?.forced

    const stepNeeded = useMemo(
        () => (currentStep ? isStepNeeded(currentStep, flow?.shown ?? [], forced) : false),
        [currentStep, isStepNeeded, flow?.shown, forced],
    )

    // Fallback for a step whose answer only became known later (e.g. Firebase settled, user reloaded)
    useEffect(() => {
        if (currentStep && stepNeeded === false) advance(currentStep, false)
    }, [currentStep, stepNeeded, advance])

    const visible = Boolean(currentStep && stepNeeded)

    function renderStep() {
        if (!visible) return null
        switch (currentStep) {
            case OnboardingStep.PickLocation:
                return <PickLocationStep onDone={() => advance(OnboardingStep.PickLocation, true)} onClose={closeFlow} />
            case OnboardingStep.PushPermission:
                return (
                    <PushPermissionStep
                        onDone={(result) => advance(OnboardingStep.PushPermission, true, result)}
                        onBack={locationStepShown ? goBackToLocation : undefined}
                    />
                )
            case OnboardingStep.Done:
                return <OnboardingDoneStep pushResult={flow?.pushResult} onDone={() => advance(OnboardingStep.Done, true)} />
            default:
                return null
        }
    }

    // One drawer for the whole flow, returned as an element (not a component) so it is never
    // remounted: the steps swap their content inside the open drawer
    const onboardingFlow: ReactNode = (
        <OnboardingDrawerShell open={visible} onClose={closeFlow}>
            {renderStep()}
        </OnboardingDrawerShell>
    )

    return { onboardingFlow, currentStep }
}
